import {
    IQueryContext,
    IQueryContextDatasourceColumn,
} from 'src/app/services/query-context/models/IQueryContext';
import {
    DatasourceColumnId,
    ICompiledSQLTokens,
    IDatasourceSQLMap,
    mergeSqlTokensArray,
} from 'src/app/utils/datasource-sql-map/datasource';
import { DatePickerSqlTokensCompiler } from 'src/app/utils/datasource-sql-map/date-picker-sql-tokens-compiler';
import { multipleSelectComponentCompileSQLTokens } from 'src/app/utils/datasource-sql-map/utils';
import { GcpProjectId } from 'src/app/utils/gcp-project-id.model';
import { buildSplitGroupSQL, buildWhereSQL } from 'src/app/utils/query/sql';
import * as sqlUtils from 'src/app/utils/sql';

export class BiVizRealtimeCloudSaveOthersDatasource {
    public static sqlMap: IDatasourceSQLMap = {
        dataset: 'bi_viz',
        tableId: 'realtime_cloud_save_others',
        columns: {
            // ACTIVITY_DATE: {
            //     columnId: 'ACTIVITY_DATE',
            //     columnSQL: 'time_hour',
            //     compileSQLTokens: (state) => {
            //         return DatePickerSqlTokensCompiler.compileTokens(
            //             'DATE(time_hour)',
            //             state
            //         );
            //     },
            // },
            ACTIVITY_DATETIME: {
                columnId: 'ACTIVITY_DATETIME',
                columnSQL: 'time_hour',
                compileSQLTokens: (state) => {
                    return DatePickerSqlTokensCompiler.compileTokens(
                        'DATE(time_hour)',
                        state
                    );
                },
            },
            ACTIVITY_HOUR: {
                columnId: 'ACTIVITY_HOUR',
                columnSQL: 'hour',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'hour',
                        state
                    );
                },
            },
            ACTIVITY_WEEKDAY: {
                columnId: 'ACTIVITY_WEEKDAY',
                columnSQL: 'FORMAT_DATETIME("%A", time_hour)',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'FORMAT_DATETIME("%A", time_hour)',
                        state
                    );
                },
            },
            ACTIVITY_APP_VERSION: {
                columnId: 'ACTIVITY_APP_VERSION',
                columnSQL: 'app_version',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'app_version',
                        state
                    );
                },
            },
            FIREBASE_APP_STORE: {
                columnId: 'FIREBASE_APP_STORE',
                columnSQL: 'app_store',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'app_store',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_EUROPEAN_UNION: {
                columnId: 'FIREBASE_COUNTRY_EUROPEAN_UNION',
                columnSQL: 'european_union',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'european_union',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_TIER: {
                columnId: 'FIREBASE_COUNTRY_TIER',
                columnSQL: 'country_tier',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'country_tier',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY: {
                columnId: 'FIREBASE_COUNTRY',
                columnSQL: 'unified_country',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'unified_country',
                        state
                    );
                },
            },
            FIREBASE_COUNTRY_US_REGION: {
                columnId: 'FIREBASE_COUNTRY_US_REGION',
                columnSQL: 'us_region',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'us_region',
                        state
                    );
                },
            },
            EVENT_PROJECT_ID: {
                columnId: 'EVENT_PROJECT_ID',
                columnSQL: 'project_id',
                compileSQLTokens: (state) => {
                    return multipleSelectComponentCompileSQLTokens(
                        'project_id',
                        state
                    );
                },
            },
            // SALES_INAPP_SOURCE: {
            //     columnId: 'SALES_INAPP_SOURCE',
            //     columnSQL: 'PurchaseComplete_Source',
            //     compileSQLTokens: (state) => {
            //         return multipleSelectComponentCompileSQLTokens(
            //             'PurchaseComplete_Source',
            //             state
            //         );
            //     },
            // },
        },
    };

    // compile sql statements specific for this datasource
    public static compileSQLStatements(
        supportedDatasourceColumns: IQueryContextDatasourceColumn[],
        queryContextSettings: IQueryContext['settings'],
        options: { gcpProjectId: GcpProjectId },
        customCompiledSqlTokens?: Partial<ICompiledSQLTokens>
    ) {
        const datetimeTrunc = (column?: string) => {
            column =
                column ?? this.sqlMap.columns?.ACTIVITY_DATETIME!.columnSQL;
            return queryContextSettings.datetimeAggregation
                ? sqlUtils.sqlDatetimeTrunc(
                      column,
                      queryContextSettings.datetimeAggregation
                  )
                : column;
        };

        const datetimeLabel = (column?: string) => {
            column =
                column ?? this.sqlMap.columns?.ACTIVITY_DATETIME!.columnSQL;
            return queryContextSettings.datetimeAggregation
                ? sqlUtils.sqlDatetimeLabel(
                      column,
                      queryContextSettings.datetimeAggregation
                  )
                : column;
        };

        // merge tokens into single deduplicated object of type ICompiledSQLTokens
        const sqlTokens: ICompiledSQLTokens = mergeSqlTokensArray(
            supportedDatasourceColumns.map((dsCol) => {
                return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                    dsCol.state,
                    options
                );
            })
        );

        // CUSTOM FILTER FOR THIS TABLE
        // sqlTokens.where.push(`validation_flag IS NULL`);

        // unnest app_version_activity if app_version is present in splits or filters
        // TODO to powinno być rozwiązane na poziomie datasource, gdzie compileSQL zwróci atrybut join, z treścią `, UNNEST...`

        const splitSelect: string = buildSplitGroupSQL(sqlTokens.split_group);
        const splitGroup = splitSelect ? 'split_group' : '';
        const splitGroupBy = splitSelect ? 'split_group, ' : '';
        const where: string = buildWhereSQL([
            ...sqlTokens.where,
            ...(customCompiledSqlTokens?.where ?? []),
        ]);
        // const join: string = sqlTokens.join.join('\n');
        // const windowTotal = splitGroupBy ? 'PARTITION BY split_group' : '';
        const windowRunning = splitGroupBy
            ? 'PARTITION BY split_group ORDER BY day_no'
            : 'ORDER BY day_no';

        const nonCohortSpecificColumns: DatasourceColumnId[] = [
            'EVENT_PROJECT_ID',
            'SALES_INAPP_SOURCE',
        ];

        const cohortSqlTokens: ICompiledSQLTokens = mergeSqlTokensArray(
            supportedDatasourceColumns
                .filter(
                    (dsCol) => nonCohortSpecificColumns.indexOf(dsCol.id) === -1
                )
                .map((dsCol) => {
                    return this.sqlMap.columns[dsCol.id]!.compileSQLTokens!(
                        dsCol.state,
                        options
                    );
                })
        );

        const cohortSplitSelect: string = buildSplitGroupSQL(
            cohortSqlTokens.split_group,
            'cohort_split_group'
        );

        const cohortSplitGroupAlias = cohortSplitSelect
            ? 'cohort_split_group'
            : '';

        const cohortSplitGroupBy = cohortSplitSelect
            ? ', cohort_split_group'
            : '';

        return {
            datetimeTrunc,
            datetimeLabel,
            splitSelect,
            splitGroup,
            splitGroupBy,
            cohortSplitSelect,
            cohortSplitGroupAlias,
            cohortSplitGroupBy,
            where,
            // windowTotal,
            windowRunning,
            // join,
        };
    }

    public static getMetricQueryWithNullsBetweenDates(
        baseQuery: string,
        compiledSQLStatements: any,
        context: IQueryContext
    ) {
        const timestampOrDateArraySQL = ((context: IQueryContext) => {
            switch (context.settings.datetimeAggregation) {
                case 'hour':
                    return `GENERATE_TIMESTAMP_ARRAY(TIMESTAMP(MIN(date)), TIMESTAMP(MAX(date)), INTERVAL 1 HOUR) AS dates`;
                case 'day':
                    return `GENERATE_DATE_ARRAY(DATE(MIN(date)), DATE(MAX(date)), INTERVAL 1 DAY) AS dates`;
                case 'week':
                    return `GENERATE_DATE_ARRAY(DATE(MIN(date)), DATE(MAX(date)), INTERVAL 1 WEEK) AS dates`;
                case 'month':
                    return `GENERATE_DATE_ARRAY(DATE(MIN(date)), DATE(MAX(date)), INTERVAL 1 MONTH) AS dates`;
                default:
                    throw new Error(
                        `Unsupported datetimeAggregation: ${context.settings.datetimeAggregation}`
                    );
            }
        })(context);

        return `WITH base AS
        (
            ${baseQuery}
        )
        
        , dates AS
        (
          SELECT 
            ${compiledSQLStatements.splitGroupBy}DATETIME(date) AS date
          FROM
          (
            SELECT
              ${compiledSQLStatements.splitGroupBy}
              ${timestampOrDateArraySQL}
            FROM base
            ${compiledSQLStatements.splitGroupBy ? 'GROUP BY split_group' : ''}
          ), UNNEST(dates) date
        )
        
        SELECT *
        FROM base
        FULL JOIN dates
        USING(${compiledSQLStatements.splitGroupBy}date)
        ORDER BY ${compiledSQLStatements.splitGroupBy}date ASC
        `;
    }
}
